/**
 * POS Settings - Departments and Categories management
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Settings, ArrowLeft, Folder, Tag, Plus, Edit2, Trash2,
  X, ChevronRight, Palette, Hash
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { posAPI } from '../../services/api';
import { toast } from 'sonner';

// Color picker options
const COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#eab308', 
  '#84cc16', '#22c55e', '#10b981', '#14b8a6',
  '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1',
  '#8b5cf6', '#a855f7', '#d946ef', '#ec4899',
];

// Department/Category Modal
const ItemModal = ({ item, type, departments, onClose, onSave }) => {
  const [formData, setFormData] = useState(item || {
    name: '',
    description: '',
    color: '#3b82f6',
    icon: '',
    sort_order: 0,
    department_id: departments?.[0]?.id || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('El nombre es requerido');
      return;
    }
    if (type === 'category' && !formData.department_id) {
      toast.error('Selecciona un departamento');
      return;
    }

    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-card rounded-2xl border border-border shadow-xl"
      >
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h2 className="font-display font-bold text-lg">
            {item ? 'Editar' : 'Nuevo'} {type === 'department' ? 'Departamento' : 'Categoría'}
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {type === 'category' && (
            <div>
              <Label>Departamento *</Label>
              <select
                value={formData.department_id}
                onChange={(e) => setFormData({ ...formData, department_id: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                required
              >
                <option value="">Seleccionar...</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <Label>Nombre *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={type === 'department' ? 'Ej: Bebidas' : 'Ej: Refrescos'}
              required
            />
          </div>

          <div>
            <Label>Descripción</Label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Descripción opcional"
            />
          </div>

          <div>
            <Label>Color</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setFormData({ ...formData, color })}
                  className={`w-8 h-8 rounded-full transition-transform ${
                    formData.color === color ? 'ring-2 ring-offset-2 ring-primary scale-110' : ''
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          <div>
            <Label>Orden</Label>
            <Input
              type="number"
              min="0"
              value={formData.sort_order}
              onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
              placeholder="0"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
            <Button type="submit" className="flex-1" disabled={saving}>
              {saving ? 'Guardando...' : item ? 'Actualizar' : 'Crear'}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default function POSSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(null); // { type: 'department' | 'category', item?: object }
  const [expandedDept, setExpandedDept] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [deptsRes, catsRes] = await Promise.all([
        posAPI.getDepartments(),
        posAPI.getCategories()
      ]);
      
      setDepartments(deptsRes.data.departments || []);
      setCategories(catsRes.data.categories || []);
    } catch (err) {
      console.error('Error fetching settings:', err);
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDepartment = async (data) => {
    try {
      if (showModal?.item) {
        await posAPI.updateDepartment(showModal.item.id, data);
        toast.success('Departamento actualizado');
      } else {
        await posAPI.createDepartment(data);
        toast.success('Departamento creado');
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error guardando departamento');
      throw err;
    }
  };

  const handleSaveCategory = async (data) => {
    try {
      await posAPI.createCategory(data);
      toast.success('Categoría creada');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error guardando categoría');
      throw err;
    }
  };

  const handleDeleteDepartment = async (dept) => {
    const deptCategories = categories.filter(c => c.department_id === dept.id);
    if (deptCategories.length > 0) {
      toast.error('No se puede eliminar, tiene categorías asociadas');
      return;
    }
    
    if (!window.confirm(`¿Eliminar "${dept.name}"?`)) return;
    
    try {
      await posAPI.deleteDepartment(dept.id);
      toast.success('Departamento eliminado');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error eliminando departamento');
    }
  };

  const handleDeleteCategory = async (cat) => {
    if (!window.confirm(`¿Eliminar "${cat.name}"?`)) return;
    
    try {
      await posAPI.deleteCategory(cat.id);
      toast.success('Categoría eliminada');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error eliminando categoría');
    }
  };

  const getCategoriesForDept = (deptId) => {
    return categories.filter(c => c.department_id === deptId);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between p-4 max-w-6xl mx-auto">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/pos')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-display font-bold text-lg">Configuración</h1>
              <p className="text-xs text-muted-foreground">Departamentos y categorías</p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-4 space-y-6">
        {/* Departments Section */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-bold text-lg flex items-center gap-2">
              <Folder className="w-5 h-5" />
              Departamentos
            </h2>
            <Button size="sm" onClick={() => setShowModal({ type: 'department' })}>
              <Plus className="w-4 h-4 mr-1" />
              Nuevo
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Cargando...</div>
          ) : departments.length === 0 ? (
            <div className="text-center py-8 glass rounded-xl">
              <Folder className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground">No hay departamentos</p>
              <Button
                className="mt-4"
                onClick={() => setShowModal({ type: 'department' })}
              >
                Crear primer departamento
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {departments.map((dept) => {
                const deptCategories = getCategoriesForDept(dept.id);
                const isExpanded = expandedDept === dept.id;
                
                return (
                  <motion.div
                    key={dept.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="glass rounded-xl overflow-hidden"
                  >
                    <div 
                      className="p-4 flex items-center gap-3 cursor-pointer hover:bg-secondary/50"
                      onClick={() => setExpandedDept(isExpanded ? null : dept.id)}
                    >
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${dept.color}20` }}
                      >
                        <Folder className="w-5 h-5" style={{ color: dept.color }} />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{dept.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {deptCategories.length} categorías
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={(e) => { e.stopPropagation(); setShowModal({ type: 'department', item: dept }); }}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={(e) => { e.stopPropagation(); handleDeleteDepartment(dept); }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                      </div>
                    </div>

                    {/* Categories */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="border-t border-border"
                        >
                          <div className="p-3 bg-secondary/30">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-muted-foreground">Categorías</span>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setShowModal({ type: 'category', item: { department_id: dept.id } })}
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                Agregar
                              </Button>
                            </div>
                            
                            {deptCategories.length === 0 ? (
                              <p className="text-sm text-muted-foreground text-center py-2">
                                Sin categorías
                              </p>
                            ) : (
                              <div className="space-y-1">
                                {deptCategories.map((cat) => (
                                  <div
                                    key={cat.id}
                                    className="flex items-center justify-between p-2 bg-background rounded-lg"
                                  >
                                    <div className="flex items-center gap-2">
                                      <div 
                                        className="w-3 h-3 rounded-full"
                                        style={{ backgroundColor: cat.color || dept.color }}
                                      />
                                      <span className="text-sm">{cat.name}</span>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6 text-destructive"
                                      onClick={() => handleDeleteCategory(cat)}
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>

        {/* Quick Add Category */}
        {departments.length > 0 && (
          <div className="glass rounded-xl p-4">
            <h3 className="font-medium mb-2 flex items-center gap-2">
              <Tag className="w-4 h-4" />
              Agregar Categoría Rápida
            </h3>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setShowModal({ type: 'category' })}
            >
              <Plus className="w-4 h-4 mr-2" />
              Nueva Categoría
            </Button>
          </div>
        )}
      </div>

      {/* Modals */}
      <AnimatePresence>
        {showModal && (
          <ItemModal
            item={showModal.item}
            type={showModal.type}
            departments={departments}
            onClose={() => setShowModal(null)}
            onSave={showModal.type === 'department' ? handleSaveDepartment : handleSaveCategory}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
